import { useDialog } from 'muibox';
import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';

export interface liveStreamCommentsBlackList {
    onlyfansId: number;
    blackListWord: string;
}

const useSextforceLiveStreamAutoCommentDelete = () => {
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const axios = useAxios();
    const dialog = useDialog();
    const userContext = useContext(UserContext);

    const [isLoadingServiceStatus, setIsLoadingServiceStatus] = useState<boolean>(false);
    const [isLoadingCommentBlackList, setIsLoadingCommentBlackList] = useState<boolean>(false);

    const getCommentBlackList = async (): Promise<liveStreamCommentsBlackList[]> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autoCommentDelete/blacklist`;

        setIsLoadingCommentBlackList(true);

        const data = await axios
            .get(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return [];
            })
            .finally(() => {
                setIsLoadingCommentBlackList(false);
            });

        return data;
    };

    const addToCommentBlackList = async (payload: liveStreamCommentsBlackList): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autoCommentDelete/blacklist/add`;

        setIsLoadingCommentBlackList(true);

        const data = await axios
            .post(query, payload)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            })
            .finally(() => {
                setIsLoadingCommentBlackList(false);
            });

        return data;
    };

    const removeFromCommentBlackList = async (payload: liveStreamCommentsBlackList): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autoCommentDelete/blacklist/remove`;

        setIsLoadingCommentBlackList(true);

        const data = await axios
            .post(query, payload)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            })
            .finally(() => {
                setIsLoadingCommentBlackList(false);
            });

        return data;
    };

    const getServiceStatus = async (): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autoCommentDelete/service`;

        setIsLoadingServiceStatus(true);

        const data = await axios
            .get(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            })
            .finally(() => {
                setIsLoadingServiceStatus(false);
            });

        return data;
    };

    const updateServiceStatus = async (status: boolean): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autoCommentDelete/service/${status}`;

        setIsLoadingServiceStatus(true);

        const data = await axios
            .post(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            })
            .finally(() => {
                setIsLoadingServiceStatus(false);
            });

        return data;
    };

    const theCommentBlackList = useQuery(
        ['theCommentBlackList', params.userId],
        () => {
            return getCommentBlackList();
        },
        {
            staleTime: 60 * 1000 * 15, // 15 minutes
            refetchOnWindowFocus: false,
            enabled: !!(userContext.jwtToken && params.userId && settingsContext.apiKey),
        },
    );

    return {
        ...theCommentBlackList,
        getCommentBlackList,
        addToCommentBlackList,
        removeFromCommentBlackList,
        isLoadingCommentBlackList,
        getServiceStatus,
        updateServiceStatus,
        isLoadingServiceStatus,
    };
};

export default useSextforceLiveStreamAutoCommentDelete;
